import React, { useCallback, useContext, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import exportSmallTokens from '@amzn/meridian-tokens/base/icon/export-small';
import menuMeatballTokens from '@amzn/meridian-tokens/base/icon/menu-meatball';
import Alert from '@amzn/meridian/alert';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Link from '@amzn/meridian/link';
import Modal from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Toaster from '@amzn/meridian/toaster';
import UserBadge from './UserBadge';

import { EmptyTeamProxyResponse } from '../../../api/Proxy/schema';
import { ProxyRelationshipsStep } from './ProxyWidgetSteps/ProxyRelationshipsStep';
import { ShareTeamStep } from './ProxyWidgetSteps/ShareTeamStep';
import { getModalTitle, localizeErrorMessage } from './ProxyWidgetSteps/utils';
import { ProxyStep } from './ProxyWidgetSteps/StepEnum';

import DataSource from '../../../api';
import { StateContext } from '../../../GlobalStateStore';
import {
  EmptyProfile,
  EmptyTeamProfile,
  Profile,
  ProxyRelatedUser,
} from '../../../Types';
import DataState from '../../../Types/DataState';
import {
  EMPTY_PROXY_RESPONSE,
  ProxyResponse,
} from '../../../Types/ProxyResponse';
import { getCurrentWeek, renderUrl, urls } from '../../../Util';
import PerformancePortalContext from '../../PerformancePortalContext';
import Naws, { QueryVariables } from '../../../api/Naws';
import useIsAdminOrImpersonating from '../../../hooks/useIsAdminOrImpersonating';

interface TMProxyWidgetProps {
  userProfile: Profile;
}

const MAX_DISPLAYED_PROXY_COACHES = 1;
const apiNaws: typeof Naws = DataSource.getDataFeed('NAWS');

const TMProxyWidget: React.FC<TMProxyWidgetProps> = ({ userProfile }) => {
  const intl = useIntl();
  const globalState = useContext(StateContext);
  const {
    state: { inspireOptions },
  } = globalState;
  const { refetchProfiles } = useContext(PerformancePortalContext);
  const isAdminOrImpersonating = useIsAdminOrImpersonating();

  const userID = userProfile.user.userID;
  const { cscEntityPrefix } = inspireOptions.data;

  const [proxyCoaches, setProxyCoaches] = useState<ProxyRelatedUser[]>(
    userProfile.proxyCoaches
  );
  const [profile, setProfile] = useState<Profile>(EmptyProfile);
  const [profileState, setProfileState] = useState<
    (typeof DataState)[keyof typeof DataState]
  >(DataState.INITIALIZED);
  const [removeProxy, setRemoveProxy] =
    useState<ProxyResponse>(EMPTY_PROXY_RESPONSE);
  const [removeProxyState, setRemoveProxyState] = useState<
    (typeof DataState)[keyof typeof DataState]
  >(DataState.INITIALIZED);
  const [addProxy, setAddProxy] = useState<ProxyResponse>(EMPTY_PROXY_RESPONSE);
  const [addProxyState, setAddProxyState] = useState<
    (typeof DataState)[keyof typeof DataState]
  >(DataState.INITIALIZED);
  const [proxyCoachCount, setProxyCoachCount] = useState(proxyCoaches.length);
  const [proxyUserName, setProxyUserName] = useState('');
  const [proxyResponseMessage, setProxyResponseMessage] = useState('');
  const [errorToasts, setErrorToasts] = useState<Array<{ id: string }>>([]);
  const [showAddToast, setShowAddToast] = useState(false);
  const [showRemoveToast, setShowRemoveToast] = useState(false);
  const [open, setOpen] = useState(false);
  const [tmStep, setTmStep] = useState<ProxyStep>(ProxyStep.RELATIONSHIPS);
  const [selfProxyStep, setSelfProxyStep] = useState(false);
  const [loadingProxyIds, setLoadingProxyIds] = useState<Set<string>>(
    new Set()
  );

  const onClickButton = useCallback(() => {
    setProxyUserName('');
    setProxyResponseMessage('');
    setShowAddToast(false);
    setShowRemoveToast(false);
    setTmStep(ProxyStep.RELATIONSHIPS);
    setSelfProxyStep(false);
    setOpen(true);
  }, []);

  const onClose = useCallback(() => {
    setOpen(false);
    setErrorToasts([]);
    setShowAddToast(false);
    setShowRemoveToast(false);
    setTmStep(ProxyStep.RELATIONSHIPS);
    setSelfProxyStep(false);
  }, []);

  const onRemoveProxy = (proxyUserID: string): void => {
    if (userID) {
      setLoadingProxyIds(prev => new Set(prev).add(proxyUserID));
      const removeProxyInput = apiNaws.prepareQueryForRemoveProxy(
        userID,
        proxyUserID,
        cscEntityPrefix
      );

      apiNaws.removeProxy(
        removeProxyInput,
        response => {
          setRemoveProxy(response);
          setRemoveProxyState(DataState.LOADED);
          setLoadingProxyIds(new Set());
        },
        setRemoveProxyState,
        refetchProfiles
      );
    }
  };

  const onAddProxy = (): void => {
    if (userID) {
      const addProxyInput = apiNaws.prepareQueryForAddProxy(
        userID,
        proxyUserName,
        cscEntityPrefix,
        []
      );

      apiNaws.addProxy(
        addProxyInput,
        setAddProxy,
        setAddProxyState,
        refetchProfiles
      );
    }
  };

  useEffect(() => {
    if (profileState === DataState.LOADED) {
      setProxyCoaches(profile.proxyCoaches);
    }
  }, [profileState]);

  useEffect(() => {
    if (removeProxyState === DataState.LOADED && userID) {
      const { success } = removeProxy;
      if (success) {
        const queryMigrationVariables = apiNaws.prepareQuery(
          getCurrentWeek(),
          userID,
          cscEntityPrefix
        ) as QueryVariables;

        apiNaws.getProfileData(
          queryMigrationVariables,
          setProfile,
          setProfileState
        );
      }
    }
  }, [removeProxyState, userID]);

  useEffect(() => {
    if (
      profileState === DataState.LOADED &&
      addProxyState === DataState.LOADED &&
      addProxy.success
    ) {
      setOpen(false);
      setShowAddToast(true);
    }
  }, [profileState, addProxyState, addProxy.success]);

  useEffect(() => {
    if (
      profileState === DataState.LOADED &&
      removeProxyState === DataState.LOADED &&
      removeProxy.success
    ) {
      setOpen(false);
      setShowRemoveToast(true);
    }
  }, [profileState, removeProxyState, removeProxy.success]);

  useEffect(() => {
    if (removeProxyState === DataState.ERROR) {
      setProxyResponseMessage('Unable to end proxy status. Please try again.');
      setErrorToasts([{ id: 'error-toast' }]);
    } else if (removeProxyState === DataState.LOADED && !removeProxy.success) {
      const localizedMessage = localizeErrorMessage(
        intl,
        removeProxy.messageCode,
        ''
      );
      setProxyResponseMessage(localizedMessage);
      setErrorToasts([{ id: 'error-toast' }]);
    }
  }, [removeProxyState, removeProxy, intl]);

  useEffect(() => {
    if (addProxyState === DataState.LOADED) {
      const { success, messageCode } = addProxy;
      if (!success) {
        setAddProxyState(DataState.ERROR);
        const localizedMessage = localizeErrorMessage(
          intl,
          messageCode,
          proxyUserName
        );
        setProxyResponseMessage(localizedMessage);
        setErrorToasts([{ id: 'error-toast' }]);
      }
    }
  }, [addProxyState, addProxy, intl, proxyUserName]);

  useEffect(() => {
    setShowAddToast(false);
  }, [proxyUserName]);

  useEffect(() => {
    if (profileState === DataState.LOADED) {
      setProxyCoachCount(proxyCoaches.length);
      setAddProxyState(DataState.INITIALIZED);
      setRemoveProxyState(DataState.INITIALIZED);
    }
  }, [proxyCoaches, profileState]);

  const proxyCoachBadges = (
    MAX_DISPLAYED_PROXY_COACHES >= proxyCoachCount
      ? proxyCoaches
      : proxyCoaches.slice(0, MAX_DISPLAYED_PROXY_COACHES - 1)
  ).map((proxyCoach: ProxyRelatedUser) => (
    <Box key={proxyCoach.tmEntity.userName}>
      <Row spacing='none' spacingInset='200 300 200 none'>
        <Box width='34.9px' height='36px'>
          <UserBadge
            username={proxyCoach.tmEntity.userName}
            givenName={proxyCoach.tmEntity.displayName}
            tooltip
            link={
              isAdminOrImpersonating
                ? renderUrl(`${urls.dashboard}/${proxyCoach.tmEntity.userID}`)
                : undefined
            }
          />
        </Box>
      </Row>
    </Box>
  ));

  return (
    <Box>
      {showAddToast && (
        <Toaster
          toasts={[{ id: 'cs_perfportal_tm_add_tmproxy_message' }]}
          onCloseToast={() => setShowAddToast(false)}
          alignmentHorizontal='center'
        >
          {(): React.ReactElement => (
            <Alert
              type='success'
              toast={true}
              onClose={() => setShowAddToast(false)}
            >
              <Text alignment='center'>
                <FormattedMessage
                  id='cs_perfportal_tm_add_tmproxy_message'
                  defaultMessage="You've successfully shared your team with {proxyUserName} for coaching."
                  values={{ proxyUserName }}
                />
              </Text>
            </Alert>
          )}
        </Toaster>
      )}

      {showRemoveToast && (
        <Toaster
          toasts={[{ id: 'cs_perfportal_remove_proxy_success_tm' }]}
          onCloseToast={() => setShowRemoveToast(false)}
          alignmentHorizontal='center'
        >
          {(): React.ReactElement => (
            <Alert
              type='success'
              toast={true}
              onClose={() => setShowRemoveToast(false)}
            >
              <Text alignment='center'>
                <FormattedMessage
                  id='cs_perfportal_remove_proxy_success_tm'
                  defaultMessage='Proxy successfully ended.'
                />
              </Text>
            </Alert>
          )}
        </Toaster>
      )}

      {proxyResponseMessage && (
        <Toaster
          toasts={errorToasts}
          onCloseToast={() => {
            setErrorToasts([]);
            setProxyResponseMessage('');
            setRemoveProxyState(DataState.INITIALIZED);
            setAddProxyState(DataState.INITIALIZED);
          }}
          alignmentHorizontal='center'
          alignmentVertical='top'
        >
          {(toast): React.ReactElement => (
            <Alert type='error' toast={true} onClose={toast.onClose}>
              {proxyResponseMessage}
            </Alert>
          )}
        </Toaster>
      )}

      <Column spacing='300'>
        <Row>
          <Text type='h100'>
            <FormattedMessage
              id='cs_perfportal_my_proxy'
              defaultMessage='My Proxy'
            />{' '}
            {proxyCoachCount === 0 ? '' : `(${proxyCoachCount})`}
          </Text>
          <Text type='h100'>
            <Link
              onClick={onClickButton}
              type='secondary'
              data-testid='manage-proxy-button'
              data-csa-c-type='link'
              data-csa-c-slot-id='share-proxy-link-slot-id'
              data-csa-c-content-id='share-proxy-link'
            >
              <Icon tokens={exportSmallTokens} />{' '}
              <FormattedMessage
                id='cs_perfportal_manage'
                defaultMessage='Manage'
              />
            </Link>
          </Text>
        </Row>

        <Row
          width='90%'
          widths='44px'
          alignmentHorizontal='start'
          wrap='down'
          spacing='none'
        >
          {proxyCoachCount > 0 ? (
            <>{proxyCoachBadges}</>
          ) : (
            <FormattedMessage
              id='cs_perfportal_no_proxy'
              defaultMessage='You are not sharing your team'
            />
          )}
        </Row>

        <Modal
          title={getModalTitle(intl, false, tmStep, selfProxyStep)}
          open={open}
          onClose={onClose}
          scrollContainer='viewport'
          aria-describedby='modal-description'
          width={
            selfProxyStep ? 450 : tmStep === ProxyStep.RELATIONSHIPS ? 600 : 450
          }
          bodySpacingInset='400 400 500'
        >
          {selfProxyStep ? (
            <ShareTeamStep
              proxyCoachCount={proxyCoachCount}
              proxyCoaches={proxyCoaches}
              proxyUserName={proxyUserName}
              addProxyState={addProxyState}
              removeProxyState={removeProxyState}
              profileState={profileState}
              setProxyUserName={setProxyUserName}
              onRemoveProxy={onRemoveProxy}
              onAddProxy={onAddProxy}
              onClickFooterButton={onClose}
            />
          ) : (
            <ProxyRelationshipsStep
              teamProxy={EmptyTeamProxyResponse}
              teamProxyState={DataState.LOADED}
              loadingProxyIds={loadingProxyIds}
              proxyResponseMessage={proxyResponseMessage}
              onRemoveOMProxy={() => {}}
              onRemoveProxy={onRemoveProxy}
              onClose={onClose}
              setStep={setTmStep}
              setRemoveProxyState={setRemoveProxyState}
              teamProfile={EmptyTeamProfile}
              userProfile={userProfile}
              cscEntityPrefix={cscEntityPrefix}
              onAssignProxyForSelf={() => setSelfProxyStep(true)}
              showTMProxies={false}
            />
          )}
        </Modal>
      </Column>
    </Box>
  );
};

export default TMProxyWidget;
